import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

import {
  Avatar,
  Button,
  ConfigProvider,
  Dropdown,
  Layout,
  Tooltip,
  theme,
} from 'antd';

import {
  DownOutlined,
  LockOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { userData } from '@/common/custom-date-selector-list/helpers';
import { AdminFooter, AdminSidebar } from '@/components';
import { showToast } from '@/utils/toastMessage/ToastMessage';
import { adminContentStyle, adminHeaderStyle, adminLayoutStyle } from './style';
import { adminAuth } from '@/api/login';
import { handleError } from '@/api/crud/handleError';
import '@/assets/style/admin/base.scss';
import '@/assets/style/main.css';
import { AdminLogout } from '@/utils/common';
import ChangePassword from '@/components/modals/ChangePassword';
import { useMedia } from '@/hooks';
import GlobalModal from '@/utils/modal/Modal';

const { Header, Footer, Content } = Layout;

const handleLogout = async (navigate, role) => {
  try {
    const res = await adminAuth.logout(role);
    showToast(res.data.response, 'success');
    AdminLogout(navigate, role);
  } catch (error) {
    handleError(error);
    throw new Error(error);
  }
};

const AdminDashboardLayout = () => {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] =
    useState(false);
  const navigate = useNavigate();
  const isMobile = useMedia('mobile');
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const primaryColor = '#189766c2';
  // Darker green used for the top header bar.
  const headerColor = '#0f7350';
  // const borderRadius =
  const backgroundColor = '#fff';

  const handleOk = () => {
    const role = Cookies.get('role');
    setOpen(false);
    handleLogout(navigate, role);
  };
  const handleCancel = () => {
    setOpen(false);
  };

  const openModal = () => {
    setOpen(true);
  };

  const { pathname } = useLocation();

  const role = Cookies.get('role');
  const displayName = userData?.companyName || 'Administrator';
  const displaySub = userData?.branchName || role || 'Admin panel';

  const items = [
    {
      key: 'user-info',
      type: 'group',
      label: (
        <div className="admin-user-menu__header">
          <Avatar
            size={40}
            style={{ backgroundColor: '#189766', flexShrink: 0 }}
            icon={<UserOutlined />}
          />
          <div className="admin-user-menu__meta">
            <span className="admin-user-menu__name">{displayName}</span>
            <span className="admin-user-menu__sub">{displaySub}</span>
          </div>
        </div>
      ),
    },
    { type: 'divider' },
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: 'Change Password',
      onClick: () => setIsChangePasswordModalVisible(true),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      danger: true,
      label: 'Logout',
      onClick: () => openModal(true),
    },
  ];

  useEffect(() => {
    if (pathname?.includes('transaction/bill-set-up/')) {
      setCollapsed(true);
    } else {
      setCollapsed(false);
    }
  }, [pathname]);

  return (
    <ConfigProvider
      theme={{
        token: {
          // Seed Token
          colorPrimary: primaryColor,
          // borderRadius: borderRadius,

          // Alias Token
          colorBgContainer: backgroundColor,
        },
        components: {
          Button: {
            // colorPrimary: primaryColor,
            // borderRadius: 2,
          },
          DatePicker: {
            colorPrimary: primaryColor,
            // borderRadius: 'none',
          },
          Input: {
            colorPrimary: primaryColor,
            // borderRadius: 'none',
          },
          Table: {
            headerBg: primaryColor,
            headerColor: '#fff',
          },
        },
      }}
    >
      <GlobalModal
        isVisible={open}
        onClose={handleCancel}
        onConfirm={handleOk}
        type="logout"
      />

      <ChangePassword
        visible={isChangePasswordModalVisible}
        onCancel={() => setIsChangePasswordModalVisible(false)}
      />
      <Layout
        style={{
          ...adminLayoutStyle,
        }}
      >
        <AdminSidebar
          collapsed={collapsed}
          colorBgContainer={backgroundColor}
          setCollapsed={setCollapsed}
        />

        <Layout>
          <Header
            className="admin-header"
            style={{
              ...adminHeaderStyle,
              background: headerColor,
            }}
          >
            <div className="admin-header__inner">
              <Tooltip title={collapsed ? 'Expand menu' : 'Collapse menu'}>
                <Button
                  type="text"
                  className="admin-header__icon-btn"
                  icon={
                    collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
                  }
                  onClick={() => setCollapsed(!collapsed)}
                />
              </Tooltip>

              <div className="admin-header__right">
                <Dropdown
                  menu={{ items }}
                  trigger={['click']}
                  placement="bottomRight"
                  overlayClassName="admin-user-dropdown"
                >
                  <button className="admin-header__profile" type="button">
                    <Avatar
                      size={34}
                      style={{ backgroundColor: '#fff', color: headerColor }}
                      icon={<UserOutlined />}
                    />
                    {/* <span className="admin-header__profile-name">
                      {displayName}
                    </span> */}
                    <DownOutlined className="admin-header__profile-caret" />
                  </button>
                </Dropdown>
              </div>
            </div>
          </Header>
          <Content
            style={{
              ...adminContentStyle,
              minHeight: isMobile ? 'calc(100dvh - 150px)' : '',
            }}
          >
            {/* CONTENT */}

            {/* <UserContextProvider> */}
            <main>
              <Outlet />
            </main>
            {/* </UserContextProvider> */}
          </Content>
          <Footer
            style={{
              backgroundColor: colorBgContainer,
            }}
          >
            <AdminFooter />
          </Footer>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};

export default AdminDashboardLayout;
